import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { formatDate, formatEuro } from '../../lib/format';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { StatutEcheanceBadge } from './StatutEcheanceBadge';
import { EcheanceWizardModal } from './EcheanceWizardModal';
import { PaiementClientModal } from './PaiementClientModal';

interface Props {
  venteId: number;
}

export function EcheancierTab({ venteId }: Props): JSX.Element {
  const [wizardOpen, setWizardOpen] = useState(false);
  const [paiementFor, setPaiementFor] = useState<{
    echeanceId: number | null;
    montantCentimes: number;
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['echeances', venteId],
    queryFn: () => api.echeances.list(venteId),
  });

  if (isLoading) return <p className="text-sm text-slate-500">Chargement…</p>;

  const items = data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200">Échéancier</h3>
        <div className="flex gap-2">
          {items.length === 0 && (
            <Button size="sm" onClick={(): void => setWizardOpen(true)}>
              Générer un échéancier
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="Aucun échéancier"
          description="Cette vente n'a pas d'échéancier explicite. Cliquez sur « Générer un échéancier » pour répartir le total en N fois."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-800">
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">Échéance</th>
                <th className="py-2 pr-3 text-right">Montant</th>
                <th className="py-2 pr-3 text-right">Payé</th>
                <th className="py-2 pr-3 text-right">Restant</th>
                <th className="py-2 pr-3">Statut</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((e) => (
                <tr key={e.id} className="border-b border-slate-800/50">
                  <td className="py-2 pr-3">{e.ordre}</td>
                  <td className="py-2 pr-3">{formatDate(e.date_prevue)}</td>
                  <td className="py-2 pr-3 text-right">{formatEuro(e.montant_ttc_centimes)}</td>
                  <td className="py-2 pr-3 text-right">{formatEuro(e.paye_centimes)}</td>
                  <td className="py-2 pr-3 text-right">{formatEuro(e.restant_centimes)}</td>
                  <td className="py-2 pr-3">
                    <StatutEcheanceBadge statut={e.statut_effectif} />
                  </td>
                  <td className="py-2 pr-3 text-right">
                    {e.statut_effectif !== 'Payee' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(): void =>
                          setPaiementFor({
                            echeanceId: e.id,
                            montantCentimes: e.restant_centimes,
                          })
                        }
                      >
                        Payer
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EcheanceWizardModal
        isOpen={wizardOpen}
        onClose={(): void => setWizardOpen(false)}
        venteId={venteId}
      />

      {paiementFor !== null && (
        <PaiementClientModal
          isOpen={true}
          onClose={(): void => setPaiementFor(null)}
          venteId={venteId}
          echeanceId={paiementFor.echeanceId}
          defaultMontantCentimes={paiementFor.montantCentimes}
        />
      )}
    </div>
  );
}
